import tokens, {
  elevationTiers,
  badgeStatePalettes,
  fonts,
  palettes,
  typeScale,
  shareTypeScale,
  radii,
  space,
  shadowPalettes,
  tierPalettes
} from './tokens'

export * from './tokens'

const kebab = value => value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

// ---------------------------------------------------------------------------
// Helpery domenowe
// ---------------------------------------------------------------------------

// Próg z 5-stopniowej skali wysokości szczytu (0–999 / … / 4000+).
export function elevationTier(elevation) {
  const meters = Math.max(0, Math.floor(Number(elevation) || 0))
  return elevationTiers.find(tier => meters >= tier.min && meters <= tier.max) ||
    elevationTiers[0]
}

export const BADGE_STATES = ['notStarted', 'inProgress', 'earned']

// Wygląd odznaki dla stanu postępu i wagi (zwykła / szlachetna).
// Wygląd stanu odznaki jako zmienne CSS (--pl-badge-*), więc przełącza się z
// motywem; brak wartości w danym stanie (np. ringColor niezaczętej) = null.
const badgeVarStates = Object.fromEntries(Object.entries(badgeStatePalettes.dark).map(([variant, states]) => [
  variant,
  Object.fromEntries(Object.entries(states).map(([state, look]) => [
    state,
    Object.fromEntries(Object.entries(look).map(([prop, value]) => [
      prop,
      value === null || value === undefined ? null : `var(--pl-badge-${variant}-${kebab(state)}-${kebab(prop)})`
    ]))
  ]))
]))

export const badgeStates = badgeVarStates

export function badgeAppearance(state, { noble = false } = {}) {
  const variant = noble ? badgeVarStates.noble : badgeVarStates.regular
  return variant[state] || variant.notStarted
}

// Styl kafelka z logotypem odznaki gotowy do :style.
export function badgeTileStyle(state, { noble = false } = {}) {
  const look = badgeAppearance(state, { noble })
  return {
    background: look.tileBackground,
    boxShadow: look.tileShadow,
    filter: look.tileFilter,
    color: look.iconColor
  }
}

// Deklaracja `font` dla wpisu ze skali typografii.
export function fontShorthand(entry) {
  const family = fonts[entry.family] || fonts.body
  return `${entry.weight} ${entry.size}px/${entry.lineHeight} ${family}`
}

// Styl tekstu z wpisu skali typografii gotowy do :style.
export function typeStyle(entry) {
  const style = { font: fontShorthand(entry) }
  if (entry.letterSpacing) style.letterSpacing = `${entry.letterSpacing}px`
  if (entry.uppercase) style.textTransform = 'uppercase'
  return style
}

// ---------------------------------------------------------------------------
// Zmienne CSS (--pl-*) generowane z tokenów
// ---------------------------------------------------------------------------


function flatten(prefix, object, out) {
  Object.entries(object).forEach(([key, value]) => {
    const name = `${prefix}-${kebab(key)}`
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flatten(name, value, out)
    } else if (typeof value === 'string' || typeof value === 'number') {
      out[name] = value
    }
  })
  return out
}

function typeVars(prefix, scale, out) {
  Object.entries(scale).forEach(([key, entry]) => {
    const name = `${prefix}-${kebab(key)}`
    out[name] = fontShorthand(entry)
    out[`${name}-ls`] = entry.letterSpacing ? `${entry.letterSpacing}px` : 'normal'
    out[`${name}-case`] = entry.uppercase ? 'uppercase' : 'none'
  })
  return out
}

const px = value => (typeof value === 'number' ? `${value}px` : value)

export function themeCssVariables(theme = 'dark') {
  const vars = {}
  const colors = palettes[theme] || palettes.dark

  vars['--pl-color-scheme'] = theme === 'light' ? 'light' : 'dark'
  const { share, ...appColors } = colors
  flatten('--pl-color', appColors, vars)
  flatten('--pl-share', {
    clayBackground: share.clay.background,
    mossBackground: share.moss.background,
    divider: share.divider,
    dividerSoft: share.dividerSoft
  }, vars)

  const tiers = tierPalettes[theme] || tierPalettes.dark
  elevationTiers.forEach(tier => {
    vars[`--pl-tier-${tier.key}-color`] = tiers[tier.key].color
    vars[`--pl-tier-${tier.key}-bg`] = tiers[tier.key].background
    vars[`--pl-tier-${tier.key}-glow`] = tiers[tier.key].glow || 'none'
  })

  Object.entries(badgeStatePalettes[theme] || badgeStatePalettes.dark).forEach(([variant, states]) => {
    Object.entries(states).forEach(([state, look]) => {
      Object.entries(look).forEach(([prop, value]) => {
        if (value === null || value === undefined) return
        vars[`--pl-badge-${variant}-${kebab(state)}-${kebab(prop)}`] = value
      })
    })
  })

  vars['--pl-font-display'] = fonts.display
  vars['--pl-font-body'] = fonts.body
  typeVars('--pl-type', typeScale, vars)
  typeVars('--pl-share-type', shareTypeScale, vars)

  Object.entries(radii).forEach(([key, value]) => {
    vars[`--pl-radius-${kebab(key)}`] = px(value)
  })
  Object.entries(space).forEach(([key, value]) => {
    vars[`--pl-space-${kebab(key)}`] = px(value)
  })
  Object.entries(shadowPalettes[theme] || shadowPalettes.dark).forEach(([key, value]) => {
    vars[`--pl-shadow-${kebab(key)}`] = value
  })

  return vars
}

// Motyw ciemny to domyślny (:root) i zakres [data-theme="dark"] (np. karty
// share w jasnej aplikacji); jasny nadpisuje tylko zmienne, które się różnią.
export function themeCssText() {
  const dark = themeCssVariables('dark')
  const light = themeCssVariables('light')
  const block = (selector, vars) => `${selector} {\n${Object.entries(vars).map(([name, value]) => `  ${name}: ${value};`).join('\n')}\n}`
  const lightOnly = Object.fromEntries(Object.entries(light).filter(([name, value]) => dark[name] !== value))
  return `${block(':root,\n[data-theme="dark"]', dark)}\n${block('[data-theme="light"]', lightOnly)}`
}

// Wstrzykuje zmienne --pl-* do <head> (raz, przed zamontowaniem aplikacji).
export function installTheme(doc = document) {
  const id = 'pl-theme-tokens'
  let style = doc.getElementById(id)
  if (!style) {
    style = doc.createElement('style')
    style.id = id
    doc.head.appendChild(style)
  }
  style.textContent = themeCssText()
}

// Composable dla komponentów (SVG, style inline, logika progów/stanów).
export function useTheme() {
  return {
    ...tokens,
    elevationTier,
    badgeAppearance,
    badgeTileStyle,
    typeStyle
  }
}
